import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Bot } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { useAiAgentProfile } from '../../hooks/useAiAgentProfile';

export default function AIAgentSettingsPage() {
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useAiAgentProfile();

  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (profile) {
      setName(profile.name || '');
      setAvatarUrl(profile.avatar_url || '');
      setDirty(false);
    }
  }, [profile]);

  const saveMutation = useMutation({
    mutationFn: (payload: { name: string; avatar_url: string }) =>
      api.aiAgent.updateProfile(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-agent-profile'] });
      setDirty(false);
      toast.success('AI Agent profile saved');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  const handleSave = () => {
    saveMutation.mutate({ name: name.trim(), avatar_url: avatarUrl.trim() });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  /* NO HEADER AND NO BACK ARROW HERE. The workspace above owns the h1 and the
     tab strip names this tab; the surface's band owns the way out. This page
     used to draw its own "< Settings" button on top of both. */
  return (
    <div className="min-h-full px-6 py-4 space-y-6 max-w-2xl">
        {/* Avatar Preview */}
        <div className="flex items-center gap-4">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name || 'AI Agent'}
              className="w-16 h-16 rounded-full object-cover border-2 border-fieldiq-border"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div
            className={`w-16 h-16 rounded-full bg-fieldiq-accent/20 flex items-center justify-center border-2 border-fieldiq-border ${avatarUrl ? 'hidden' : ''}`}
          >
            <Bot size={28} className="text-fieldiq-accent" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{name || 'Unnamed Agent'}</p>
            <p className="text-xs text-fieldiq-muted">Your company's AI assistant</p>
          </div>
        </div>

        {/* Agent Name */}
        <div>
          <label className="text-sm font-medium text-white block mb-1">
            Agent Name
          </label>
          <p className="text-xs text-fieldiq-muted mb-2">
            The name your team sees when interacting with the AI agent
          </p>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            placeholder="e.g. FieldIQ Assistant"
            className="w-full max-w-sm bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50"
          />
        </div>

        {/* Avatar URL */}
        <div>
          <label className="text-sm font-medium text-white block mb-1">
            Avatar URL
          </label>
          <p className="text-xs text-fieldiq-muted mb-2">
            A direct link to an image for your agent's avatar.{' '}
            <a
              href="https://www.dicebear.com/playground/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-fieldiq-accent hover:underline"
            >
              Generate one with DiceBear
            </a>
          </p>
          <input
            type="text"
            value={avatarUrl}
            onChange={(e) => {
              setAvatarUrl(e.target.value);
              setDirty(true);
            }}
            placeholder="https://api.dicebear.com/9.x/bottts/svg?seed=fieldiq"
            className="w-full max-w-lg bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent/50"
          />
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={!dirty || saveMutation.isPending}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            dirty
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {saveMutation.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : null}
          {saveMutation.isPending ? 'Saving...' : 'Save'}
      </button>
    </div>
  );
}
